
import React, { useState, useEffect } from 'react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useAuth } from '@/hooks/useAuth';
import { internalProfileService } from '@/services/internalProfileService';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Save, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import new components
import { CoachProfilePhotoSection } from './coach/CoachProfilePhotoSection';
import { CoachBasicInfoSection } from './coach/CoachBasicInfoSection';
import { CoachingInfoSection } from './coach/CoachingInfoSection';
import { CoachSpecializationsSection } from './coach/CoachSpecializationsSection';
import { CoachSportsPreferencesSection } from './coach/CoachSportsPreferencesSection';
import { CoachAvailabilitySection } from './coach/CoachAvailabilitySection';
import { CoachPrivacySettingsSection } from './coach/CoachPrivacySettingsSection';
import { CoachSportsAndTypeSection } from './coach/CoachSportsAndTypeSection';
import { CoachConnectionsSection } from './coach/CoachConnectionsSection';
import { usePoints } from '@/hooks/usePoints';

const CoachProfileForm: React.FC = () => {
  const { user: internalUser, profile: internalProfile, refreshProfile: refreshInternalProfile } = useInternalAuth();
  const { user: supabaseUser, profile: supabaseProfile } = useAuth();
  const { checkAndAwardProfileCompletion } = usePoints();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Determine which user type we're dealing with
  const isInternalUser = !!internalUser && !supabaseUser;
  const user = internalUser || supabaseUser;
  const profile = internalProfile || supabaseProfile;

  const [formData, setFormData] = useState({
    // Basic info
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    bio: '',
    profile_photo_url: '',
    
    // Coach-specific fields
    years_coaching: '',
    coaching_style: '',
    coaching_certifications: '',
    specializations: [] as string[],
    previous_teams: '',
    preferred_sports: [] as string[],
    primary_sport: '',
    coach_type: '',
    availability: [] as string[],
    connected_players: [] as string[],
    connected_coaches: [] as string[],
    connected_teams: [] as string[],
    show_profile_public: false,
    show_contact_public: false,
  });

  useEffect(() => {
    console.log('CoachProfileForm - user:', user);
    console.log('CoachProfileForm - profile:', profile);
    
    if (user && profile) {
      const userData = user as any;
      const profileData = profile as any;
      
      console.log('Setting form data with profile photo URL:', profileData.profile_photo_url);
      
      setFormData({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        username: profileData.username || userData.username || '',
        phone: profileData.phone || '',
        bio: profileData.bio || '',
        profile_photo_url: profileData.profile_photo_url || '',
        years_coaching: profileData.years_coaching?.toString() || '',
        coaching_style: profileData.coaching_style || '',
        coaching_certifications: profileData.coaching_certifications || '',
        specializations: profileData.specializations || [],
        previous_teams: profileData.previous_teams || '',
        preferred_sports: profileData.preferred_sports || [],
        primary_sport: profileData.primary_sport || '',
        coach_type: profileData.coach_type || '',
        availability: profileData.availability || [],
        connected_players: Array.isArray(profileData.connected_players) ? profileData.connected_players : [],
        connected_coaches: Array.isArray(profileData.connected_coaches) ? profileData.connected_coaches : [],
        connected_teams: Array.isArray(profileData.connected_teams) ? profileData.connected_teams : [],
        show_profile_public: profileData.show_profile_public || false,
        show_contact_public: profileData.show_contact_public || false,
      });
    }
  }, [user, profile]);

  // Use useCallback to prevent function recreation that might cause re-renders
  const handleInputChange = React.useCallback((name: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handlePhotoUpdated = async (newPhotoPath: string | null) => {
    console.log('Photo updated:', newPhotoPath);
    setFormData(prev => ({ ...prev, profile_photo_url: newPhotoPath || '' }));
    
    // Update the profile in the database immediately
    if (user && newPhotoPath !== null) {
      try {
        if (isInternalUser) {
          await internalProfileService.updateProfilePhoto(user.id, newPhotoPath);
          await refreshInternalProfile();
        } else {
          const { error } = await supabase
            .from('profiles')
            .update({ profile_photo_url: newPhotoPath })
            .eq('id', user.id);
          
          if (error) {
            console.error('Failed to update profile photo:', error);
          }
        }
      } catch (error) {
        console.error('Error updating profile photo:', error);
      }
    }
  };

  const handleSpecializationChange = React.useCallback((specialization: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specializations: checked 
        ? [...prev.specializations, specialization]
        : prev.specializations.filter(s => s !== specialization)
    }));
  }, []);

  const handleSportsChange = React.useCallback((sport: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      preferred_sports: checked 
        ? [...prev.preferred_sports, sport]
        : prev.preferred_sports.filter(s => s !== sport)
    }));
  }, []);

  const handleAvailabilityChange = React.useCallback((day: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      availability: checked 
        ? [...prev.availability, day]
        : prev.availability.filter(d => d !== day)
    }));
  }, []);

  // Fixed connection handlers to prevent form state reset
  const handleConnectionChange = React.useCallback((type: 'player' | 'coach' | 'team', action: 'add' | 'remove', identifier: string) => {
    const fieldName = `connected_${type}s` as keyof typeof formData;
    
    setFormData(prev => {
      const currentList = prev[fieldName] as string[];
      let newList: string[];
      
      if (action === 'add' && !currentList.includes(identifier)) {
        newList = [...currentList, identifier];
      } else if (action === 'remove') {
        newList = currentList.filter(item => item !== identifier);
      } else {
        return prev; // No change needed
      }
      
      return { 
        ...prev, 
        [fieldName]: newList 
      };
    });
  }, []);

  const getInitials = () => {
    const firstName = formData.first_name?.[0] || '';
    const lastName = formData.last_name?.[0] || '';
    return (firstName + lastName).toUpperCase() || formData.username?.[0]?.toUpperCase() || 'U';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('No user found. Please log in again.');
      return;
    }

    setLoading(true);
    
    try {
      console.log('Saving coach profile for user:', user.id);

      if (isInternalUser) {
        // Save to internal user profiles
        const profileData = {
          user_id: user.id,
          role: 'coach',
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          bio: formData.bio,
          profile_photo_url: formData.profile_photo_url,
          years_coaching: formData.years_coaching ? parseInt(formData.years_coaching) : null,
          coaching_style: formData.coaching_style,
          coaching_certifications: formData.coaching_certifications,
          specializations: formData.specializations,
          previous_teams: formData.previous_teams,
          preferred_sports: formData.preferred_sports,
          primary_sport: formData.primary_sport,
          coach_type: formData.coach_type,
          availability: formData.availability,
          connected_players: formData.connected_players,
          connected_coaches: formData.connected_coaches,
          connected_teams: formData.connected_teams,
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
        };

        console.log('Saving internal user profile with photo URL:', formData.profile_photo_url);
        console.log('Complete profile data being saved:', profileData);
        const result = await internalProfileService.upsertProfile(profileData);

        if (result.success) {
          toast.success('Coach profile saved successfully!');
          await refreshInternalProfile();
          
          // Check for profile completion and award points if applicable
          try {
            const savedProfile = { ...profileData, role: 'coach' };
            const wasAwarded = await checkAndAwardProfileCompletion(savedProfile, 'coach');
            if (wasAwarded) {
              toast.success('🎉 Profile Complete! +50 Points Earned!', { 
                duration: 5000,
                description: 'Your coaching profile is now 100% complete!'
              });
            }
          } catch (error) {
            console.error('Error checking profile completion:', error);
          }
          
          setTimeout(() => {
            navigate('/internal-profile');
          }, 1000);
        } else {
          console.error('Failed to save internal profile:', result.error);
          toast.error(result.error || 'Failed to save profile');
        }
      } else {
        // Save to Supabase profiles
        const profileData = {
          id: user.id,
          email: (user as any).email || '',
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          bio: formData.bio,
          profile_photo_url: formData.profile_photo_url,
          role: 'coach',
          years_coaching: formData.years_coaching ? parseInt(formData.years_coaching) : null,
          coaching_style: formData.coaching_style,
          coaching_certifications: formData.coaching_certifications,
          specializations: formData.specializations,
          previous_teams: formData.previous_teams,
          preferred_sports: formData.preferred_sports,
          primary_sport: formData.primary_sport,
          coach_type: formData.coach_type,
          availability: formData.availability,
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
        };

        console.log('Saving Supabase user profile with photo URL:', formData.profile_photo_url);
        const { error } = await supabase
          .from('profiles')
          .upsert(profileData, { onConflict: 'id' });

        if (error) {
          console.error('Failed to save Supabase profile:', error);
          toast.error('Failed to save profile: ' + error.message);
        } else {
          toast.success('Coach profile saved successfully!');
          
          // Check for profile completion and award points if applicable
          try {
            const savedProfile = { ...profileData, role: 'coach' };
            const wasAwarded = await checkAndAwardProfileCompletion(savedProfile, 'coach');
            if (wasAwarded) {
              toast.success('🎉 Profile Complete! +50 Points Earned!', { 
                duration: 5000,
                description: 'Your coaching profile is now 100% complete!'
              });
            }
          } catch (error) {
            console.error('Error checking profile completion:', error);
          }
          
          setTimeout(() => {
            navigate('/profile');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to the appropriate profile view page
    if (isInternalUser) {
      navigate('/internal-profile');
    } else {
      navigate('/profile');
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            Coach Profile
          </CardTitle>
          <CardDescription className="text-gray-400">
            Complete your coaching profile to connect with teams and players.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <CoachProfilePhotoSection
              currentPhotoPath={formData.profile_photo_url}
              userId={user.id}
              fallbackInitials={getInitials()}
              onPhotoUpdated={handlePhotoUpdated}
              disabled={loading}
            />

            <CoachBasicInfoSection
              formData={formData}
              onInputChange={handleInputChange}
            />

            <CoachSportsAndTypeSection
              primarySport={formData.primary_sport}
              coachType={formData.coach_type}
              onPrimarySportChange={(sport) => handleInputChange('primary_sport', sport)}
              onCoachTypeChange={(type) => handleInputChange('coach_type', type)}
            />

            <CoachingInfoSection
              formData={formData}
              onInputChange={handleInputChange}
            />

            <CoachSpecializationsSection
              specializations={formData.specializations}
              onSpecializationChange={handleSpecializationChange}
            />

            <CoachSportsPreferencesSection
              preferredSports={formData.preferred_sports}
              onSportsChange={handleSportsChange}
            />

            <CoachConnectionsSection
              connectedPlayers={formData.connected_players}
              connectedCoaches={formData.connected_coaches}
              connectedTeams={formData.connected_teams}
              onAddConnection={(type, identifier) => handleConnectionChange(type, 'add', identifier)}
              onRemoveConnection={(type, identifier) => handleConnectionChange(type, 'remove', identifier)}
            />

            <CoachAvailabilitySection
              availability={formData.availability}
              onAvailabilityChange={handleAvailabilityChange}
            />

            <CoachPrivacySettingsSection
              showProfilePublic={formData.show_profile_public}
              showContactPublic={formData.show_contact_public}
              onPrivacyChange={handleInputChange}
            />

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-gray-600 text-gray-400 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Coach Profile
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachProfileForm;
